import { Autocomplete as BaseAutocomplete } from '@base-ui/react';
import * as React from 'react';

import { BASE_UI_OVERLAY_POP_MOTION } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

/** Free-form text input with optional suggestions. */
export const Autocomplete = BaseAutocomplete.Root;

export const AutocompleteInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentPropsWithoutRef<typeof BaseAutocomplete.Input>
>(({ className, ...props }, ref) => (
  <BaseAutocomplete.Input
    ref={ref}
    className={cn(
      'flex h-11 w-full rounded-md border border-border/60 bg-transparent px-3 py-1 text-base transition-colors placeholder:text-muted-foreground/60 md:h-9 md:text-sm',
      'focus-visible:border-ring/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/30',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  />
));
AutocompleteInput.displayName = 'AutocompleteInput';

type AutocompleteContentProps = React.ComponentPropsWithoutRef<typeof BaseAutocomplete.Popup> & {
  portalContainer?: React.ComponentPropsWithoutRef<typeof BaseAutocomplete.Portal>['container'];
};

export const AutocompleteContent = React.forwardRef<HTMLDivElement, AutocompleteContentProps>(
  ({ className, portalContainer, ...props }, ref) => (
    <BaseAutocomplete.Portal container={portalContainer}>
      <BaseAutocomplete.Positioner
        className="z-popover max-w-[calc(100vw-1rem)] outline-hidden"
        sideOffset={4}
      >
        <BaseAutocomplete.Popup
          ref={ref}
          className={cn(
            'w-[var(--anchor-width)] min-w-[12rem] overflow-hidden rounded-xl border border-border/60 bg-popover/95 backdrop-blur-xl shadow-elevation-3',
            BASE_UI_OVERLAY_POP_MOTION,
            'data-[side=bottom]:data-open:slide-in-from-top-1 data-[side=top]:data-open:slide-in-from-bottom-1',
            className,
          )}
          {...props}
        />
      </BaseAutocomplete.Positioner>
    </BaseAutocomplete.Portal>
  ),
);
AutocompleteContent.displayName = 'AutocompleteContent';

export const AutocompleteList = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseAutocomplete.List>
>(({ className, ...props }, ref) => (
  <BaseAutocomplete.List
    ref={ref}
    className={cn('max-h-64 overflow-y-auto p-1', className)}
    {...props}
  />
));
AutocompleteList.displayName = 'AutocompleteList';

export const AutocompleteItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseAutocomplete.Item>
>(({ className, children, ...props }, ref) => (
  <BaseAutocomplete.Item
    ref={ref}
    className={cn(
      'relative flex min-h-11 w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-caption font-normal text-foreground/85 outline-none transition-colors sm:min-h-0',
      'data-[highlighted]:bg-muted/60 data-[highlighted]:text-foreground',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    {children}
  </BaseAutocomplete.Item>
));
AutocompleteItem.displayName = 'AutocompleteItem';

export const AutocompleteEmpty = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof BaseAutocomplete.Empty>
>(({ className, ...props }, ref) => (
  <BaseAutocomplete.Empty
    ref={ref}
    className={cn('px-2 py-3 text-detail text-muted-foreground', className)}
    {...props}
  />
));
AutocompleteEmpty.displayName = 'AutocompleteEmpty';
